<template>
<div>
  <div class="px-4 sm:px-0 mb-6">
    <h2 class="text-2xl font-bold tracking-tight text-gray-900">{{ $gettext("Tableau de bord") }}</h2>
    <p class="mt-1 text-sm text-gray-500">{{ $gettext("Voici un résumé des données globales.") }}</p>
  </div>

  <!-- Statistics -->
  <div class="mb-6">
    <StatisticsOverview />
  </div>

  <!-- Bottom grid: Recent clients + Risk distribution -->
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-5">
    <!-- Recent clients -->
    <div class="lg:col-span-3 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 class="text-sm font-semibold text-gray-900">{{ $gettext("Clients récents") }}</h3>
        <router-link :to="{ name: 'customers-overview' }" class="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
          {{ $gettext("Voir tout") }} &rarr;
        </router-link>
      </div>
      <ul role="list" class="divide-y divide-gray-100">
        <li
          v-for="customer in customerStore.customers.slice(0, 5)"
          :key="customer.id"
          class="flex items-center justify-between px-6 py-3.5 hover:bg-gray-50 transition-colors"
        >
          <CustomerLink :customer="customer" />
          <RiskScoringLabel :level="customer.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
        </li>
        <li v-if="!customerStore.customers.length" class="px-6 py-8 text-center text-sm text-gray-400">
          {{ $gettext("Aucun client pour le moment.") }}
        </li>
      </ul>
    </div>

    <!-- Risk distribution -->
    <div class="lg:col-span-2 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
      <div class="px-6 py-4 border-b border-gray-100">
        <h3 class="text-sm font-semibold text-gray-900">{{ $gettext("Répartition des risques") }}</h3>
      </div>
      <div class="px-6 py-5">
        <v-chart class="chart h-44" :option="option" autoresize />
        <div class="mt-4 space-y-3">
          <div v-for="item in riskItems" :key="item.level" class="flex items-center justify-between">
            <div class="flex items-center gap-2.5">
              <span :class="[item.dotClass, 'h-2.5 w-2.5 rounded-full']" />
              <span class="text-sm text-gray-600">{{ item.label }}</span>
            </div>
            <span :class="[item.badgeClass, 'min-w-[2rem] text-center rounded-full px-2.5 py-0.5 text-xs font-semibold']">
              {{ scoringOverview.countForRiskLevel(item.level) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script setup>
import CustomerLink from "@/components/customers/CustomerLink.vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart from "vue-echarts";
import { ref, watchEffect, watch, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import { useCustomerStore } from "@/stores/customer.js";
import { useScoringOverviewStore } from "@/stores/scoringOverview.js";
import StatisticsOverview from "./stats/StatisticsOverview.vue";
import { useGettext } from "vue3-gettext";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const { $gettext } = useGettext();
const customerStore = useCustomerStore();
const scoringOverview = useScoringOverviewStore();
const route = useRoute();
const customerDashboardIncludes = ["lastRiskScoringLevel", "saleListingAgreementsCount", "rentalListingAgreementsCount"];

const riskItems = computed(() => [
  {
    level: "low",
    label: $gettext("Risque faible"),
    dotClass: "bg-emerald-500",
    badgeClass: "bg-emerald-50 text-emerald-700",
  },
  {
    level: "medium",
    label: $gettext("Risque standard"),
    dotClass: "bg-amber-400",
    badgeClass: "bg-amber-50 text-amber-700",
  },
  {
    level: "high",
    label: $gettext("Risque élevé"),
    dotClass: "bg-red-500",
    badgeClass: "bg-red-50 text-red-700",
  },
]);

const option = ref({
  color: ["#10b981", "#f59e0b", "#ef4444"],
  tooltip: {
    trigger: "item",
    formatter: "{b}: {c} ({d}%)",
  },
  series: [
    {
      type: "pie",
      radius: ["40%", "58%"],
      center: ["50%", "65%"],
      top: "-100%",
      bottom: "-100%",
      padAngle: 2,
      startAngle: 180,
      endAngle: 360,
      itemStyle: {
        borderRadius: 4,
      },
      labelLine: {
        show: false
      },
      label: {
        show: false,
        position: "center"
      },
      emphasis: {
        scale: true,
        scaleSize: 6,
      },
      data: []
    }
  ]
});

watchEffect(() => {
  if (scoringOverview.overview.length) {
    option.value.series[0].data = [
      { value: scoringOverview.countForRiskLevel("low"), name: $gettext("Faible") },
      { value: scoringOverview.countForRiskLevel("medium"), name: $gettext("Standard") },
      { value: scoringOverview.countForRiskLevel("high"), name: $gettext("Élevé") },
    ];
  }
});

const refreshRiskOverview = async () => {
  await scoringOverview.fetchRiskOverview();
};

onMounted(async () => {
  await customerStore.fetchAll(null, customerDashboardIncludes);
  await customerStore.watchSourceChanges(() => {
    customerStore.fetchAll(null, customerDashboardIncludes);
  });
  await scoringOverview.watchResourceChanges(() => {
    scoringOverview.fetchRiskOverview();
  });
});

watch(route, refreshRiskOverview, { immediate: true });
</script>
